import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { useAccount, useChainId, useWriteContract } from "wagmi";
import {
  SOCIAL_RECOVERY_MODULE_ADDRESS,
  SOCIAL_RECOVERY_MODULE_ABI,
} from "../utils/Constants";
import { readContract, http, createConfig } from "@wagmi/core";
import { baseSepolia } from "@wagmi/core/chains";
import ConnectWalletButton from "./ConnectWalletButton";
import {
  CONTRACT_CONFIG,
  isSupportedChain,
  getContractAddress,
  getChainName,
  SUPPORTED_CHAINS,
} from "../utils/chainConfig";

function FinalizeRecoveryForm({}) {
  const [walletAddress, setWalletAddress] = useState("");
  const [walletAddressError, setWalletAddressError] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const { isConnected } = useAccount();
  const chainId = useChainId();

  const { writeContract, isPending, isSuccess, isError, error } =
    useWriteContract();

  // Add chain support check
  useEffect(() => {
    if (chainId && !isSupportedChain(chainId)) {
      setErrorMessage(
        `Please switch to a supported chain. Current chain (${getChainName(
          chainId
        )}) is not supported.`
      );
    } else {
      setErrorMessage("");
    }
  }, [chainId]);

  const handleWalletAddressChange = (e) => {
    const value = e.target.value;
    setWalletAddress(value);

    if (!ethers.isAddress(value)) {
      setWalletAddressError("Invalid AXR wallet address.");
    } else {
      setWalletAddressError("");
    }
  };

  const fetchRecoveryDetails = async () => {
    try {
      if (!isSupportedChain(chainId)) {
        setErrorMessage(
          `Please switch to a supported chain. Current chain (${getChainName(
            chainId
          )}) is not supported.`
        );
        return;
      }

      const contractAddress = getContractAddress(chainId);
      const config = createConfig({
        chains: [SUPPORTED_CHAINS.find((chain) => chain.id === chainId)],
        transports: {
          [chainId]: http(),
        },
      });

      const recoveryData = await readContract(config, {
        address: contractAddress,
        abi: CONTRACT_CONFIG.abi,
        functionName: "recoveries",
        args: [walletAddress],
      });

      if (Number(recoveryData[1]) === 0) {
        setErrorMessage("No pending recovery transactions.");
        setRecovery(null);
      } else {
        setRecovery(recoveryData);
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Error fetching recovery details:", error);
      setErrorMessage("Error fetching recovery details. Please try again.");
    }
  };

  const handleFinalize = async () => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      if (!isConnected) {
        setErrorMessage("Please connect your wallet first.");
        return;
      }

      if (!isSupportedChain(chainId)) {
        setErrorMessage(
          `Please switch to a supported chain. Current chain (${getChainName(
            chainId
          )}) is not supported.`
        );
        return;
      }

      const contractAddress = getContractAddress(chainId);

      await writeContract({
        address: contractAddress,
        abi: CONTRACT_CONFIG.abi,
        functionName: "executeRecovery",
        args: [walletAddress],
      });
    } catch (error) {
      console.error("Error finalizing recovery:", error);
      setErrorMessage("Error finalizing recovery. Please try again.");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setSuccessMessage("Recovery finalized successfully!");
    }

    if (isError) {
      setErrorMessage(`Error finalizing recovery: ${error.message}`);
    }
  }, [isSuccess, isError, error]);

  return (
    <div className="finalize-form">
      <h2>Finalize Recovery Transaction</h2>
      <ConnectWalletButton />

      <div className="form-group">
        <label>AXR Wallet Address:</label>
        <input
          type="text"
          value={walletAddress}
          onChange={handleWalletAddressChange}
          required
        />
        {walletAddressError && (
          <p className="error-message">{walletAddressError}</p>
        )}
      </div>
      <button
        onClick={fetchRecoveryDetails}
        disabled={!!walletAddressError || !walletAddress}
      >
        Fetch Existing Recovery TXs
      </button>
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {recovery && (
        <div className="recovery-details">
          <h3>Current Recovery TX</h3>
          <p>New Owner: {recovery[0]}</p>
          <p>Guardian Threshold: {Number(recovery[1])}</p>
          <p>
            Execution Time:{" "}
            {new Date(Number(recovery[2]) * 1000).toLocaleString()}
          </p>
          <p>Executed: {recovery[3] ? "Yes" : "No"}</p>
          {Number(recovery[2]) * 1000 <= Date.now() && !recovery[3] && (
            <button onClick={handleFinalize} disabled={isPending}>
              {isPending ? "Finalizing..." : "Finalize Recovery"}
            </button>
          )}
          {Number(recovery[2]) * 1000 > Date.now() && (
            <p className="info-message">
              Please wait until{" "}
              {new Date(Number(recovery[2]) * 1000).toLocaleString()} to
              finalize the recovery transaction.
            </p>
          )}
          {recovery[3] && (
            <p className="info-message">
              Recovery transaction already executed. Please create a new one if
              needed.
            </p>
          )}
        </div>
      )}
      {successMessage && <p className="success-message">{successMessage}</p>}
    </div>
  );
}

export default FinalizeRecoveryForm;
